<script>
import {ElMessage, ElMessageBox, ElNotification} from "element-plus";
import moment from "moment";
import MfPriceInput from "@/components/core/MfPriceInput.vue";
import BoardTimeline from "./BoardTimeline.vue";
import {
  boardTypeOptions,
  boardTypeLabels,
  boardStageLabels,
  boardDeputyRequiredTypes,
  boardTitleLangKeys,
  boardTitleLangLabels,
} from "./boardTypes";

// Canonical list of the 5 sequential windows — drives the range-picker
// computed props, the validateDateOrder() check and the timeline (06-board-
// crud-reference.md: natural sequence accept -> review -> promotion? ->
// voting -> deputy?).
const WINDOWS = [
  {key: "accept", label: "Qabul qilish oynasi", startKey: "accept_start_date", endKey: "accept_end_date", color: "#2563eb"},
  {key: "review", label: "Ko'rib chiqish oynasi", startKey: "review_start_date", endKey: "review_end_date", color: "#d97706"},
  {key: "promotion", label: "Promotion oynasi", startKey: "promotion_start_date", endKey: "promotion_end_date", color: "#7c3aed"},
  {key: "voting", label: "Ovoz berish oynasi", startKey: "voting_start_date", endKey: "voting_end_date", color: "#16a34a"},
  {key: "deputy", label: "Deputat oynasi", startKey: "deputy_start_date", endKey: "deputy_end_date", color: "#db2777"},
];

const LANG_TABS = [
  {code: "uz_lat", tabLabel: "UZ"},
  {code: "uz_cyr", tabLabel: "UZ Krill"},
  {code: "ru", tabLabel: "RU"},
  {code: "en", tabLabel: "EN"},
  {code: "kr", tabLabel: "KR"},
];

const TASHKENT_OFFSET = "+05:00";
const TASHKENT_OFFSET_MIN = 300;

const RFC3339_TZ = /T\d{2}:\d{2}:\d{2}(\.\d+)?([+-]\d{2}:\d{2}|Z)$/;
const PLAIN_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/;

const BOARD_DATE_FIELDS = [
  "accept_start_date", "accept_end_date",
  "review_start_date", "review_end_date",
  "promotion_start_date", "promotion_end_date",
  "voting_start_date", "voting_end_date",
  "deputy_start_date", "deputy_end_date",
  "partnership_end_date",
  "edit_permission_end_date",
];

function toTashkentWall(value) {
  if (value === null || value === undefined || value === "") return "";
  if (value instanceof Date) {
    return moment(value).utcOffset(TASHKENT_OFFSET_MIN).format("YYYY-MM-DDTHH:mm:ss");
  }
  const str = String(value);
  if (PLAIN_DATETIME.test(str)) return str.slice(0, 19);
  const m = moment.parseZone(str);
  if (!m.isValid()) return "";
  return m.utcOffset(TASHKENT_OFFSET_MIN).format("YYYY-MM-DDTHH:mm:ss");
}

// PUT /v1/boards/:id uchun: har doim timezone bilan.
function formatForUpdate(value) {
  const wall = toTashkentWall(value);
  return wall ? `${wall}${TASHKENT_OFFSET}` : "";
}

function formatForCreate(value) {
  return toTashkentWall(value);
}

const requiredRule = {required: true, message: "Majburiy maydon", trigger: "blur"};
const requiredChangeRule = {required: true, message: "Majburiy maydon", trigger: "change"};

const emptyTitleLang = () =>
    boardTitleLangKeys.reduce((acc, key) => {
      acc[key] = "";
      return acc;
    }, {});

function makeRangeComputed() {
  const result = {};
  WINDOWS.forEach((w) => {
    result[`${w.key}Range`] = {
      get() {
        return [this.form[w.startKey] || null, this.form[w.endKey] || null];
      },
      set(val) {
        this.form[w.startKey] = val?.[0] || "";
        this.form[w.endKey] = val?.[1] || "";
      },
    };
  });
  return result;
}

export default {
  name: "BoardForm",
  components: {MfPriceInput, BoardTimeline},
  data() {
    const titleLangRules = boardTitleLangKeys.reduce((acc, key) => {
      acc[`title_lang.${key}`] = [requiredRule];
      return acc;
    }, {});

    return {
      loading: false,
      submitting: false,
      activeLang: "uz_lat",
      langErrors: [],
      langTabs: LANG_TABS,
      duplicateSourceTitle: "",
      savedDeputyLimit: null,
      boardTypeOptions,
      boardTypeLabels,
      boardStageLabels,
      boardTitleLangKeys,
      boardTitleLangLabels,
      form: {
        id: null,
        title: "",
        title_lang: emptyTitleLang(),
        type: "",
        year: new Date().getFullYear(),
        season: 1,
        accept_start_date: "",
        accept_end_date: "",
        review_start_date: "",
        review_end_date: "",
        promotion_start_date: "",
        promotion_end_date: "",
        voting_start_date: "",
        voting_end_date: "",
        partnership_end_date: "",
        deputy_start_date: "",
        deputy_end_date: "",
        edit_permission_end_date: "",
        partnership_budget: null,
        initiatives_max_amount: null,
        equipment_initiatives_max_amount: null,
        // Deputat byudjeti — initiatives_max_amount'dan MUTLAQO alohida maydon
        // (u bitta loyihaning shifti, bu esa bitta deputatning bitta okrugdagi
        // g'olib tanlovlari yig'indisining shifti).
        deputy_limit_amount: null,
        is_active: true,
      },
      readOnlyInfo: {
        stage: "",
        total_debt: null,
        total_amount: null,
        partnership_used_budget: null,
        initiatives_count: null,
        // Serverda hisoblanadi (now < edit_permission_end_date). Faqat ko'rsatish
        // uchun — mahalliy soat bo'yicha qayta hisoblamaymiz.
        can_grant_edit_permission: null,
      },
      rules: {
        title: [
          requiredRule,
          {min: 5, message: "Kamida 5 belgi bo'lishi kerak", trigger: "blur"},
        ],
        ...titleLangRules,
        type: [requiredChangeRule],
        year: [requiredChangeRule],
        season: [requiredChangeRule],
        partnership_end_date: [requiredChangeRule],
      },
    };
  },
  computed: {
    ...makeRangeComputed(),
    isEdit() {
      return !!this.$route.params.id;
    },
    duplicateSourceId() {
      return this.$route.query.duplicate_id ? parseInt(this.$route.query.duplicate_id) : null;
    },
    isDuplicate() {
      return !this.isEdit && !!this.duplicateSourceId;
    },
    isDeputyType() {
      return boardDeputyRequiredTypes.includes(this.form.type);
    },
    // Bo'sh input va 0 — ikki xil narsa: bo'sh = maydon yuborilmaydi (PUT'da
    // eski qiymat qoladi), 0 = limit ataylab o'chiriladi.
    deputyLimitBlank() {
      return this.form.deputy_limit_amount === null || this.form.deputy_limit_amount === "";
    },
    deputyLimitZero() {
      return !this.deputyLimitBlank && Number(this.form.deputy_limit_amount) === 0;
    },
    // Mavjud tanlovlar bekor qilinmaydi, lekin yangi tanlov bloklanadi —
    // limitni pasaytirayotgan adminni ogohlantiramiz.
    isDeputyLimitLowered() {
      if (!this.isEdit || !this.savedDeputyLimit) return false;
      if (this.deputyLimitBlank || this.deputyLimitZero) return false;
      return Number(this.form.deputy_limit_amount) < Number(this.savedDeputyLimit);
    },
    // Saqlashdan oldin tasdiq so'raladigan holatlar: yangi mavsumda limit yo'q,
    // yoki mavjud mavsumda limit ataylab 0 ga tushirilmoqda.
    needsDeputyLimitConfirm() {
      if (!this.isDeputyType) return false;
      if (!this.isEdit) return this.deputyLimitBlank || this.deputyLimitZero;
      return this.deputyLimitZero && !!this.savedDeputyLimit;
    },
    formRules() {
      const rules = {
        ...this.rules,
        accept_start_date: [this.rangeRule("accept_start_date", "accept_end_date")],
        review_start_date: [this.rangeRule("review_start_date", "review_end_date")],
        promotion_start_date: [this.rangeRule("promotion_start_date", "promotion_end_date")],
        voting_start_date: [this.rangeRule("voting_start_date", "voting_end_date")],
      };
      if (this.isDeputyType) {
        rules.deputy_start_date = [this.rangeRule("deputy_start_date", "deputy_end_date")];
      }
      return rules;
    },
    timelineSequence() {
      const list = [];
      WINDOWS.forEach((w) => {
        if (w.key === "deputy" && !this.isDeputyType) return;
        const start = this.form[w.startKey];
        const end = this.form[w.endKey];
        if (start && end) list.push({...w, start: new Date(start), end: new Date(end)});
      });
      return list;
    },
    timelineConflictKeys() {
      const keys = new Set();
      const list = this.timelineSequence;
      for (let i = 1; i < list.length; i++) {
        if (list[i].start < list[i - 1].end) {
          keys.add(list[i - 1].key);
          keys.add(list[i].key);
        }
      }
      return keys;
    },
    timelineWarnings() {
      const warnings = [];
      const list = this.timelineSequence;
      for (let i = 1; i < list.length; i++) {
        if (list[i].start < list[i - 1].end) {
          warnings.push(`"${list[i].label}" oynasi "${list[i - 1].label}" tugamasdan oldin boshlanadi`);
        }
      }
      return warnings;
    },
    timelineSegments() {
      return this.timelineSequence.map((w) => ({
        key: w.key,
        label: w.label,
        color: w.color,
        conflict: this.timelineConflictKeys.has(w.key),
        startLabel: this.$moment(w.start).format("DD.MM.YYYY HH:mm"),
        endLabel: this.$moment(w.end).format("DD.MM.YYYY HH:mm"),
      }));
    },
    timelineMarker() {
      if (!this.form.partnership_end_date) return null;
      return {
        label: "Sherikchilik oynasi tugashi",
        dateLabel: this.$moment(this.form.partnership_end_date).format("DD.MM.YYYY HH:mm"),
      };
    },
  },
  methods: {
    rangeRule(startKey, endKey) {
      return {
        validator: (rule, value, callback) => {
          if (this.form[startKey] && this.form[endKey]) callback();
          else callback(new Error("Oyna sanalarini tanlang"));
        },
        trigger: "change",
      };
    },
    clearLangError(code) {
      this.langErrors = this.langErrors.filter((c) => c !== code);
    },
    validateDateOrder() {
      const errors = [];
      WINDOWS.forEach((w) => {
        const start = this.form[w.startKey];
        const end = this.form[w.endKey];
        if (start && end && new Date(start) >= new Date(end)) {
          errors.push(`${w.label}: boshlanish sanasi tugash sanasidan oldin bo'lishi kerak`);
        }
      });
      // Server qoidasi: review_start_date < edit_permission_end_date <= voting_start_date
      // (chegaralar assimetrik). Shu yerda ushlanmasa — 400 qaytadi.
      const deadline = this.form.edit_permission_end_date;
      if (deadline) {
        const reviewStart = this.form.review_start_date;
        const votingStart = this.form.voting_start_date;
        if (reviewStart && new Date(deadline) <= new Date(reviewStart)) {
          errors.push(
              "Tahrirlash ruxsati muddati \"Ko'rib chiqish oynasi\" boshlanishidan keyin bo'lishi kerak"
          );
        }
        if (votingStart && new Date(deadline) > new Date(votingStart)) {
          errors.push(
              "Tahrirlash ruxsati muddati \"Ovoz berish oynasi\" boshlanishidan kechikmasligi kerak"
          );
        }
      }
      return errors;
    },
    // Kunlik aniqlik — el-date-picker soatni cheklay olmaydi, aniq chegaralarni
    // validateDateOrder() ushlaydi.
    disabledEditPermissionDate(date) {
      const dayStart = (value) => {
        const d = new Date(value);
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
      };
      if (this.form.review_start_date && date < dayStart(this.form.review_start_date)) return true;
      if (this.form.voting_start_date && date > dayStart(this.form.voting_start_date)) return true;
      return false;
    },
    assertUpdatePayloadDates(payload) {
      for (const f of BOARD_DATE_FIELDS) {
        const v = payload[f];
        if (typeof v === "string" && v !== "" && !RFC3339_TZ.test(v)) {
          throw new Error(`${f} timezone'siz ketmoqda: "${v}" — formatForUpdate() ishlatilmagan`);
        }
      }
    },
    buildPayload() {
      // Update -> timezone MAJBURIY, Create -> timezone'SIZ (yuqoridagi helperlar).
      const formatBoardDate = this.isEdit ? formatForUpdate : formatForCreate;
      const payload = {
        title: this.form.title,
        title_lang: {...this.form.title_lang},
        type: this.form.type,
        year: Number(this.form.year),
        season: Number(this.form.season),
        accept_start_date: formatBoardDate(this.form.accept_start_date),
        accept_end_date: formatBoardDate(this.form.accept_end_date),
        review_start_date: formatBoardDate(this.form.review_start_date),
        review_end_date: formatBoardDate(this.form.review_end_date),
        promotion_start_date: formatBoardDate(this.form.promotion_start_date),
        promotion_end_date: formatBoardDate(this.form.promotion_end_date),
        voting_start_date: formatBoardDate(this.form.voting_start_date),
        voting_end_date: formatBoardDate(this.form.voting_end_date),
        partnership_end_date: formatBoardDate(this.form.partnership_end_date),
      };
      if (this.isDeputyType) {
        payload.deputy_start_date = formatBoardDate(this.form.deputy_start_date);
        payload.deputy_end_date = formatBoardDate(this.form.deputy_end_date);
      }
      if (this.form.partnership_budget !== null && this.form.partnership_budget !== "") {
        payload.partnership_budget = Number(this.form.partnership_budget);
      }
      if (this.form.initiatives_max_amount !== null && this.form.initiatives_max_amount !== "") {
        payload.initiatives_max_amount = Number(this.form.initiatives_max_amount);
      }
      if (
          this.form.equipment_initiatives_max_amount !== null &&
          this.form.equipment_initiatives_max_amount !== ""
      ) {
        payload.equipment_initiatives_max_amount = Number(
            this.form.equipment_initiatives_max_amount
        );
      }
      // Deputat limiti faqat deputat oynasi bor turlarda ishlatiladi. Bo'sh
      // qoldirilsa maydon UMUMAN yuborilmaydi: PUT'da bu "o'zgartirma" (server
      // COALESCE qiladi), POST'da esa server 0 qo'yadi — 0 = limit sozlanmagan,
      // deputat g'olib tanlay olmaydi (shuning uchun pastda ogohlantiramiz).
      // BIGINT so'm kutiladi, shuning uchun kasr qismi kesiladi.
      if (
          this.isDeputyType &&
          this.form.deputy_limit_amount !== null &&
          this.form.deputy_limit_amount !== ""
      ) {
        payload.deputy_limit_amount = Math.trunc(Number(this.form.deputy_limit_amount));
      }
      // Moderator ruxsat muddati boshqa sanalardan farq qiladi: bo'sh qiymatni
      // `""` yoki `null` qilib yuborish PUT da "o'zgartirma" degani, ya'ni
      // tozalash jimgina ishlamay qoladi. Tozalash faqat clear flag orqali,
      // va ikkala maydon hech qachon birga ketmaydi (400 beradi).
      if (this.form.edit_permission_end_date) {
        payload.edit_permission_end_date = formatBoardDate(this.form.edit_permission_end_date);
      } else if (this.isEdit) {
        payload.clear_edit_permission_end_date = true;
      }
      if (this.isEdit) {
        payload.is_active = this.form.is_active;
      }
      return payload;
    },
    handleApiError(error, fallbackTitle = "Xatolik") {
      const data = error?.response?.data;
      const message = data?.message || "Iltimos, qayta urinib ko'ring.";
      if (data?.type === "CONFLICT") {
        ElNotification({
          title: "Bunday mavsum allaqachon mavjud",
          message,
          type: "warning",
        });
      } else if (data?.type === "BADREQUEST") {
        ElNotification({title: "Noto'g'ri ma'lumot kiritildi", message, type: "error"});
      } else if (data?.type === "NOTFOUND") {
        ElNotification({title: "Mavsum topilmadi", message, type: "error"});
      } else {
        ElNotification({title: fallbackTitle, message, type: "error"});
      }
    },
    onSubmit() {
      this.langErrors = [];
      this.$refs.form.validate((valid, invalidFields) => {
        if (!valid) {
          ElMessage.error("Iltimos, majburiy maydonlarni to'ldiring");
          if (invalidFields) {
            this.langErrors = Object.keys(invalidFields)
                .filter((key) => key.startsWith("title_lang."))
                .map((key) => key.split(".")[1]);
            const firstInvalidLang = this.langErrors[0];
            if (firstInvalidLang) this.activeLang = firstInvalidLang;
          }
          return;
        }
        const orderErrors = this.validateDateOrder();
        if (orderErrors.length) {
          orderErrors.forEach((msg) => ElMessage.error(msg));
          return;
        }

        const payload = this.buildPayload();
        if (this.isEdit) {
          try {
            this.assertUpdatePayloadDates(payload);
          } catch (e) {
            ElMessage.error(e.message);
            return;
          }
        }

        // Limitsiz mavsumda deputatlar tanlash bosqichida jimgina bloklanadi —
        // jo'natishdan oldin aniq so'raymiz.
        if (this.needsDeputyLimitConfirm) {
          ElMessageBox.confirm(
              this.isEdit
                  ? "Deputat limiti 0 ga tushirilmoqda — deputatlar bu mavsumda yangi "
                  + "g'olib loyiha tanlay olmaydi. Baribir saqlansinmi?"
                  : "Deputat limiti kiritilmagan (0 bo'lib saqlanadi) — deputatlar bu "
                  + "mavsumda g'olib loyiha tanlay olmaydi. Baribir saqlansinmi?",
              "Deputat limiti sozlanmagan",
              {
                confirmButtonText: "Saqlash",
                cancelButtonText: "Orqaga qaytish",
                type: "warning",
              }
          )
              .then(() => this.sendBoard(payload))
              .catch(() => {});
          return;
        }

        this.sendBoard(payload);
      });
    },
    sendBoard(payload) {
      this.submitting = true;
      const request = this.isEdit
          ? this.$http.put(`api/v1/boards/${this.form.id}`, payload)
          : this.$http.post("api/v1/boards", payload);

      request
          .then(() => {
            ElNotification({
              title: "Tabriklaymiz",
              message: "Mavsum muvaffaqiyatli saqlandi",
              type: "success",
            });
            this.$router.push("/app/boards");
          })
          .catch((error) => {
            this.handleApiError(error, "Saqlashda xatolik");
          })
          .finally(() => {
            this.submitting = false;
          });
    },
    // `is_active` afterwards (see getBoard / loadDuplicateSource).
    applyBoardData(data) {
      this.form.title = data.title || "";
      this.form.title_lang = boardTitleLangKeys.reduce((acc, key) => {
        acc[key] = data.title_lang?.[key] || "";
        return acc;
      }, {});
      this.form.type = data.type || "";
      this.form.year = data.year;
      this.form.season = data.season;
      this.form.accept_start_date = toTashkentWall(data.accept_start_date);
      this.form.accept_end_date = toTashkentWall(data.accept_end_date);
      this.form.review_start_date = toTashkentWall(data.review_start_date);
      this.form.review_end_date = toTashkentWall(data.review_end_date);
      this.form.promotion_start_date = toTashkentWall(data.promotion_start_date);
      this.form.promotion_end_date = toTashkentWall(data.promotion_end_date);
      this.form.voting_start_date = toTashkentWall(data.voting_start_date);
      this.form.voting_end_date = toTashkentWall(data.voting_end_date);
      this.form.partnership_end_date = toTashkentWall(data.partnership_end_date);
      this.form.deputy_start_date = toTashkentWall(data.deputy_start_date);
      this.form.deputy_end_date = toTashkentWall(data.deputy_end_date);
      this.form.edit_permission_end_date = toTashkentWall(data.edit_permission_end_date);
      this.form.partnership_budget = data.partnership_budget ?? null;
      this.form.initiatives_max_amount = data.initiatives_max_amount ?? null;
      this.form.equipment_initiatives_max_amount = data.equipment_initiatives_max_amount ?? null;
      // Server NULL'ni 0 qilib qaytaradi; 0 = sozlanmagan, formada bo'sh ko'rsatamiz.
      this.form.deputy_limit_amount = data.deputy_limit_amount || null;
      this.savedDeputyLimit = data.deputy_limit_amount || null;

      this.readOnlyInfo.stage = data.stage || "";
      this.readOnlyInfo.total_debt = data.total_debt ?? null;
      this.readOnlyInfo.total_amount = data.total_amount ?? null;
      this.readOnlyInfo.partnership_used_budget = data.partnership_used_budget ?? null;
      this.readOnlyInfo.initiatives_count = data.initiatives_count ?? null;
      this.readOnlyInfo.can_grant_edit_permission = data.can_grant_edit_permission ?? null;
    },
    getBoard() {
      this.loading = true;
      this.$http
          .get(`api/v1/admin/boards/${this.form.id}`)
          .then(({data}) => {
            this.applyBoardData(data);
            this.form.is_active = data.is_active !== false;
          })
          .catch((error) => {
            this.handleApiError(error, "Yuklashda xatolik");
            if (error?.response?.data?.type === "NOTFOUND") {
              this.$router.push("/app/boards");
            }
          })
          .finally(() => {
            this.loading = false;
          });
    },
    loadDuplicateSource() {
      this.loading = true;
      this.$http
          .get(`api/v1/admin/boards/${this.duplicateSourceId}`)
          .then(({data}) => {
            this.applyBoardData(data);
            this.duplicateSourceTitle = data.title || "";
            // is_active intentionally left at the form's own default (true) —
            // a duplicate is a brand-new board, not a copy of the source's
            // active/inactive state.
          })
          .catch((error) => {
            this.handleApiError(error, "Yuklashda xatolik");
          })
          .finally(() => {
            this.loading = false;
          });
    },
    onTypeChange(newType) {
      if (!boardDeputyRequiredTypes.includes(newType)) {
        this.form.deputy_start_date = "";
        this.form.deputy_end_date = "";
        this.form.deputy_limit_amount = null;
      }
    },
  },
  mounted() {
    if (this.$route.params.id) {
      this.form.id = this.$route.params.id;
      this.getBoard();
    } else if (this.duplicateSourceId) {
      this.loadDuplicateSource();
    }
  },
};
</script>

<template>
  <div class="board-form" v-loading="loading">
    <h2 class="page-title">
      <template v-if="isEdit">Mavsumni tahrirlash</template>
      <template v-else-if="isDuplicate">Mavsumni nusxalash: {{ duplicateSourceTitle || "..." }}</template>
      <template v-else>Yangi mavsum qo'shish</template>
    </h2>

    <el-alert
        v-if="isDuplicate"
        type="warning"
        show-icon
        :closable="false"
        class="duplicate-hint"
        title="Bu yangi, mustaqil mavsum sifatida yaratiladi"
    >
      Yil/mavsum kombinatsiyasi asl mavsum bilan bir xil qolsa, saqlashda "bunday mavsum
      allaqachon mavjud" xatosi chiqadi — kamida bittasini o'zgartiring. Barcha sana
      maydonlari ham asl mavsumnikidan ko'chirilgan — yangi davrga mos qilib yangilang
      (pastda sariq belgi bilan ko'rsatilgan).
    </el-alert>

    <div v-if="isEdit" class="read-only-info">
      <div class="info-item">
        <span class="info-label">Joriy bosqich</span>
        <el-tag effect="plain">{{ boardStageLabels[readOnlyInfo.stage] || "Noma'lum" }}</el-tag>
      </div>
      <div class="info-item">
        <span class="info-label">Tashabbuslar soni</span>
        <span>{{ readOnlyInfo.initiatives_count ?? "-" }}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Sarflangan sherikchilik byudjeti</span>
        <span>{{
            readOnlyInfo.partnership_used_budget !== null
                ? Number(readOnlyInfo.partnership_used_budget).toLocaleString("ru-RU") + " so'm"
                : "-"
          }}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Umumiy summa</span>
        <span>{{
            readOnlyInfo.total_amount !== null
                ? Number(readOnlyInfo.total_amount).toLocaleString("ru-RU") + " so'm"
                : "-"
          }}</span>
      </div>
      <div class="info-item" v-if="readOnlyInfo.can_grant_edit_permission !== null">
        <span class="info-label">Moderator ruxsat bera oladimi</span>
        <el-tag
            effect="plain"
            :type="readOnlyInfo.can_grant_edit_permission ? 'success' : 'danger'"
        >
          {{ readOnlyInfo.can_grant_edit_permission ? "Ha" : "Yo'q — muddat tugagan" }}
        </el-tag>
      </div>
      <div class="info-item">
        <span class="info-label">Faol</span>
        <el-switch v-model="form.is_active" active-text="Ha" inactive-text="Yo'q"/>
      </div>
    </div>

    <el-row :gutter="16">
      <el-col :xs="24" :lg="16">
        <el-form
            ref="form"
            :model="form"
            :rules="formRules"
            label-position="top"
            status-icon
        >
          <fieldset class="form-fieldset">
            <legend>Asosiy ma'lumot</legend>
            <el-row :gutter="10">
              <el-col :xs="24" :sm="24" :md="12" :lg="8">
                <el-form-item label="Nomi (standart til)" prop="title">
                  <el-input v-model="form.title" placeholder="Mavsum nomini kiriting" clearable/>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="24" :md="12" :lg="8">
                <el-form-item label="Turi" prop="type">
                  <el-select
                      v-model="form.type"
                      placeholder="Turini tanlang"
                      style="width: 100%"
                      @change="onTypeChange"
                  >
                    <el-option
                        v-for="item in boardTypeOptions"
                        :key="item.value"
                        :label="item.label"
                        :value="item.value"
                    />
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :xs="12" :sm="12" :md="6" :lg="4" :class="{'needs-review': isDuplicate}">
                <el-form-item label="Yil" prop="year">
                  <el-input-number v-model="form.year" :min="2000" :max="2100" style="width: 100%"/>
                  <span v-if="isDuplicate" class="hint-text needs-review-hint">Tekshiring</span>
                </el-form-item>
              </el-col>
              <el-col :xs="12" :sm="12" :md="6" :lg="4" :class="{'needs-review': isDuplicate}">
                <el-form-item label="Mavsum raqami" prop="season">
                  <el-input-number v-model="form.season" :min="1" style="width: 100%"/>
                  <span v-if="isDuplicate" class="hint-text needs-review-hint">Tekshiring</span>
                </el-form-item>
              </el-col>
            </el-row>
          </fieldset>

          <fieldset class="form-fieldset">
            <legend>Nomi (barcha tillarda)</legend>
            <el-tabs v-model="activeLang" class="lang-tabs">
              <el-tab-pane v-for="lt in langTabs" :key="lt.code" :name="lt.code">
                <template #label>
                  <span class="lang-tab-label" :class="{'lang-tab-label--error': langErrors.includes(lt.code)}">
                    {{ lt.tabLabel }}
                    <span v-if="!form.title_lang[lt.code]" class="lang-tab-dot" title="Bo'sh"/>
                  </span>
                </template>
                <el-form-item :label="boardTitleLangLabels[lt.code]" :prop="'title_lang.' + lt.code">
                  <el-input
                      v-model="form.title_lang[lt.code]"
                      :placeholder="boardTitleLangLabels[lt.code]"
                      clearable
                      @input="clearLangError(lt.code)"
                  />
                </el-form-item>
              </el-tab-pane>
            </el-tabs>
          </fieldset>

          <fieldset class="form-fieldset" :class="{'needs-review': isDuplicate}">
            <legend>
              Qabul qilish oynasi
              <span v-if="isDuplicate" class="hint-text needs-review-hint">Bu sanalarni yangilang</span>
            </legend>
            <el-form-item prop="accept_start_date">
              <el-date-picker
                  v-model="acceptRange"
                  type="datetimerange"
                  format="DD.MM.YYYY HH:mm:ss"
                  value-format="YYYY-MM-DDTHH:mm:ss"
                  range-separator="—"
                  start-placeholder="Boshlanishi"
                  end-placeholder="Tugashi"
                  style="width: 100%"
              />
            </el-form-item>
          </fieldset>

          <fieldset class="form-fieldset" :class="{'needs-review': isDuplicate}">
            <legend>
              Ko'rib chiqish oynasi
              <span v-if="isDuplicate" class="hint-text needs-review-hint">Bu sanalarni yangilang</span>
            </legend>
            <el-form-item prop="review_start_date">
              <el-date-picker
                  v-model="reviewRange"
                  type="datetimerange"
                  format="DD.MM.YYYY HH:mm:ss"
                  value-format="YYYY-MM-DDTHH:mm:ss"
                  range-separator="—"
                  start-placeholder="Boshlanishi"
                  end-placeholder="Tugashi"
                  style="width: 100%"
              />
            </el-form-item>
          </fieldset>

          <fieldset class="form-fieldset" :class="{'needs-review': isDuplicate}">
            <legend>
              Promotion oynasi
              <span v-if="isDuplicate" class="hint-text needs-review-hint">Bu sanalarni yangilang</span>
            </legend>
            <el-form-item prop="promotion_start_date">
              <el-date-picker
                  v-model="promotionRange"
                  type="datetimerange"
                  format="DD.MM.YYYY HH:mm:ss"
                  value-format="YYYY-MM-DDTHH:mm:ss"
                  range-separator="—"
                  start-placeholder="Boshlanishi"
                  end-placeholder="Tugashi"
                  style="width: 100%"
              />
            </el-form-item>
          </fieldset>

          <fieldset class="form-fieldset" :class="{'needs-review': isDuplicate}">
            <legend>
              Ovoz berish oynasi
              <span v-if="isDuplicate" class="hint-text needs-review-hint">Bu sanalarni yangilang</span>
            </legend>
            <el-form-item prop="voting_start_date">
              <el-date-picker
                  v-model="votingRange"
                  type="datetimerange"
                  format="DD.MM.YYYY HH:mm:ss"
                  value-format="YYYY-MM-DDTHH:mm:ss"
                  range-separator="—"
                  start-placeholder="Boshlanishi"
                  end-placeholder="Tugashi"
                  style="width: 100%"
              />
            </el-form-item>
          </fieldset>

          <fieldset v-if="isDeputyType" class="form-fieldset" :class="{'needs-review': isDuplicate}">
            <legend>
              Deputat oynasi
              <span v-if="isDuplicate" class="hint-text needs-review-hint">Bu sanalarni yangilang</span>
            </legend>
            <el-form-item prop="deputy_start_date">
              <el-date-picker
                  v-model="deputyRange"
                  type="datetimerange"
                  format="DD.MM.YYYY HH:mm:ss"
                  value-format="YYYY-MM-DDTHH:mm:ss"
                  range-separator="—"
                  start-placeholder="Boshlanishi"
                  end-placeholder="Tugashi"
                  style="width: 100%"
              />
            </el-form-item>
          </fieldset>
          <p v-else-if="form.type" class="hint-text deputy-hint">
            Deputat oynasi faqat "{{ boardTypeLabels.INITIATIVE }}" va "{{ boardTypeLabels.MY_GARDEN }}"
            turlari uchun talab qilinadi — "{{ boardTypeLabels[form.type] }}" turida bu bosqich yo'q.
          </p>

          <fieldset class="form-fieldset">
            <legend>Moderator ruxsati</legend>
            <el-row :gutter="10">
              <el-col :xs="24" :sm="24" :md="12" :lg="10" :class="{'needs-review': isDuplicate && form.edit_permission_end_date}">
                <el-form-item label="Moderatorlar tahrirlash ruxsatini berish muddati">
                  <el-date-picker
                      v-model="form.edit_permission_end_date"
                      type="datetime"
                      format="DD.MM.YYYY HH:mm:ss"
                      value-format="YYYY-MM-DDTHH:mm:ss"
                      placeholder="Muddat (ixtiyoriy)"
                      clearable
                      :disabled-date="disabledEditPermissionDate"
                      style="width: 100%"
                  />
                  <span
                      v-if="isDuplicate && form.edit_permission_end_date"
                      class="hint-text needs-review-hint"
                  >Bu sanani yangilang</span>
                  <span v-else-if="!form.edit_permission_end_date" class="hint-text">
                    Bo'sh = muddat cheklanmagan
                  </span>
                </el-form-item>
              </el-col>
            </el-row>
            <p class="hint-text fieldset-note">
              Bu muddatdan keyin moderatorlar muallifga tahrirlash huquqini <b>bera olmaydi</b>.
              Allaqachon berilgan ruxsatlar bekor qilinmaydi — moderator ularni muddatdan keyin
              ham qo'lda qaytarib olishi mumkin. Muddat moderatsiya boshlanishidan keyin va
              "Ovoz berish" boshlanishidan oldin kechikmasdan bo'lishi kerak.
              Muddatni olib tashlash uchun maydonni tozalang (×).
            </p>
          </fieldset>

          <fieldset class="form-fieldset">
            <legend>Moliyaviy parametrlar</legend>
            <el-row :gutter="10">
              <el-col :xs="24" :sm="24" :md="12" :lg="8" :class="{'needs-review': isDuplicate}">
                <el-form-item label="Sherikchilik oynasi tugashi" prop="partnership_end_date">
                  <el-date-picker
                      v-model="form.partnership_end_date"
                      type="datetime"
                      format="DD.MM.YYYY HH:mm:ss"
                      value-format="YYYY-MM-DDTHH:mm:ss"
                      placeholder="Sanani tanlang"
                      style="width: 100%"
                  />
                  <span v-if="isDuplicate" class="hint-text needs-review-hint">Bu sanani yangilang</span>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="24" :md="12" :lg="8">
                <el-form-item label="Sherikchilik byudjeti">
                  <MfPriceInput v-model="form.partnership_budget" placeholder="0"/>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="24" :md="12" :lg="8">
                <el-form-item label="Tashabbus maksimal summasi">
                  <MfPriceInput v-model="form.initiatives_max_amount" placeholder="0"/>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="24" :md="12" :lg="8">
                <el-form-item label="Jihozlash tashabbusi maksimal summasi">
                  <MfPriceInput v-model="form.equipment_initiatives_max_amount" placeholder="0"/>
                </el-form-item>
              </el-col>
              <el-col v-if="isDeputyType" :xs="24" :sm="24" :md="12" :lg="8">
                <el-form-item label="Deputat limiti (bitta okrug uchun)">
                  <MfPriceInput v-model="form.deputy_limit_amount" placeholder="0"/>
                  <span v-if="deputyLimitZero" class="hint-text hint-text--warning">
                    0 = limit sozlanmagan: deputat g'olib loyiha tanlay olmaydi.
                  </span>
                  <span
                      v-else-if="deputyLimitBlank && isEdit && savedDeputyLimit"
                      class="hint-text hint-text--warning"
                  >
                    Bo'sh qoldirilsa joriy qiymat
                    ({{ Number(savedDeputyLimit).toLocaleString("ru-RU") }} so'm) o'zgarmaydi.
                    Limitni bekor qilish uchun 0 kiriting.
                  </span>
                  <span v-else-if="deputyLimitBlank" class="hint-text hint-text--warning">
                    Bo'sh qoldirilsa 0 bo'lib saqlanadi — deputat g'olib loyiha tanlay olmaydi.
                  </span>
                  <span v-else-if="isDeputyLimitLowered" class="hint-text hint-text--warning">
                    Limit oldingi qiymatdan
                    ({{ Number(savedDeputyLimit).toLocaleString("ru-RU") }} so'm) past.
                    Allaqachon tanlangan loyihalar bekor qilinmaydi, lekin deputat limitdan
                    oshib ketgan bo'lsa yangi loyiha tanlay olmaydi.
                  </span>
                </el-form-item>
              </el-col>
            </el-row>
            <p v-if="isDeputyType" class="hint-text fieldset-note">
              "Tashabbus maksimal summasi" — bitta loyihaning shifti (fuqaro va moderator uchun).
              "Deputat limiti" — bitta deputatning bitta okrugdagi g'olib tanlovlari
              yig'indisining shifti; loyihalar soni cheklanmagan, 0 qoldirilsa deputat
              g'olib loyiha tanlay olmaydi. Bular alohida maydonlar: deputat byudjetini
              o'zgartirish uchun faqat "Deputat limiti" ni tahrirlang.
            </p>
          </fieldset>

          <div class="form-actions">
            <el-button class="custom-btn" @click="() => this.$router.go(-1)">Orqaga</el-button>
            <el-button class="custom-btn" type="primary" :loading="submitting" @click="onSubmit">
              {{ isEdit ? "Saqlash" : "Yaratish" }}
            </el-button>
          </div>
        </el-form>
      </el-col>

      <el-col :xs="24" :lg="8">
        <fieldset class="form-fieldset timeline-fieldset">
          <legend>Bosqichlar vaqt chizig'i</legend>
          <BoardTimeline
              :segments="timelineSegments"
              :marker="timelineMarker"
              :warnings="timelineWarnings"
          />
        </fieldset>
      </el-col>
    </el-row>
  </div>
</template>

<style scoped lang="scss">
.page-title {
  margin: 0 0 16px;
  font-family: "Montserrat-Bold", sans-serif;
  color: #0a194e;
}

.duplicate-hint {
  margin-bottom: 16px;
}

.read-only-info {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  padding: 12px 16px;
  margin-bottom: 16px;
  background: #f5f7fb;
  border-radius: 6px;

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .info-label {
      font-size: 12px;
      color: #777777;
    }
  }
}

.form-fieldset {
  border: 1px solid rgba(137, 141, 141, 0.2);
  border-radius: 5px;
  padding: 12px 16px;
  margin-bottom: 16px;

  legend {
    display: flex;
    align-items: center;
    gap: 8px;
    font-style: italic;
    font-weight: bold;
    padding: 0 6px;
  }

  &.needs-review {
    border-color: #e6a23c;
    border-style: dashed;
  }
}

.timeline-fieldset {
  position: sticky;
  top: 12px;
}

.lang-tabs {
  margin-bottom: 4px;
}

.lang-tab-label {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.lang-tab-label--error {
  color: #f56c6c;
}

.lang-tab-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #e6a23c;
  flex-shrink: 0;
}

.hint-text {
  display: block;
  flex-basis: 100%;
  font-size: 12px;
  color: #999;
}

.hint-text--warning {
  color: #e6a23c;
  margin-top: 2px;
}

.needs-review-hint {
  color: #e6a23c;
  font-weight: 600;
  margin-top: 2px;
}

.deputy-hint {
  margin: -4px 0 16px;
  padding: 8px 12px;
  background: #f5f7fb;
  border-radius: 5px;
}

.fieldset-note {
  margin: 0;
  padding: 8px 12px;
  background: #f5f7fb;
  border-radius: 5px;
  line-height: 1.5;
}

.form-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}
</style>
